import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable, TableCellEllipsis } from "./ui";
import { MDBCol, MDBContainer, MDBRow } from "mdb-react-ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../services";
import PriceDisplay from "./PriceDisplay";
import OrderStatsCard from "./OrdersReportComponents/OrderStatsCard";
import {
  fetchShipmentReports,
  fetchShipmentReportsExport,
} from "../services/trackHubApi";
import {
  resolveDateLocale,
  formatLocalizedDateTimeParts,
} from "../utils/formatDateTime";
import { usePermission } from "../hooks/usePermission";
import {
  RBAC_MODULES,
  PERMISSION_ACTIONS,
} from "../config/routePermissions";
import "./trackhub/trackhub-reports.css";
import CourierInfoCell from "./trackhub/CourierInfoCell";

const TABS = [
  { id: "branch", i18n: "shipmentReportsPage.tabs.branch" },
  { id: "carrier", i18n: "shipmentReportsPage.tabs.carrier" },
  { id: "source", i18n: "shipmentReportsPage.tabs.source" },
];

const STATUS_CLASS = {
  Delivered: "trackhub-reports-status--delivered",
  "In Transit": "trackhub-reports-status--in-transit",
  Assigned: "trackhub-reports-status--assigned",
  Pending: "trackhub-reports-status--pending",
  Cancelled: "trackhub-reports-status--cancelled",
};

function StatusBadge({ status }) {
  const cls = STATUS_CLASS[status] || "trackhub-reports-status--pending";
  return <span className={`trackhub-reports-status ${cls}`}>{status}</span>;
}

function ShipmentIdCell({ shipmentId, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);

  if (!shipmentId) {
    return <span className="trackhub-reports-mono">—</span>;
  }

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shipmentId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy shipment id", err);
    }
  };

  return (
    <div className="trackhub-reports-id-cell" dir="ltr" onClick={(e) => e.stopPropagation()}>
      <TableCellEllipsis title={shipmentId}>{shipmentId}</TableCellEllipsis>
      <button
        type="button"
        className="trackhub-reports-copy-btn"
        onClick={handleCopy}
        title={copied ? copiedLabel : copyLabel}
        aria-label={copied ? copiedLabel : copyLabel}
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
      </button>
    </div>
  );
}

function defaultLast30Days() {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function formatOrderSource(source, t) {
  const s = String(source || "").toUpperCase();
  if (s === "MANUAL" || s.includes("MANUAL")) {
    return t("shipmentReportsPage.sources.manual");
  }
  if (s.includes("CATALOG") || s === "DIGITAL_CATALOG") {
    return t("shipmentReportsPage.sources.digitalCatalog");
  }
  if (
    s.includes("CHAT") ||
    s.includes("ORDER_AGENT") ||
    s === "WHATSAPP_ORDER_AGENT"
  ) {
    return t("shipmentReportsPage.sources.orderAgent");
  }
  return source || "—";
}

function ShipmentReportsPage() {
  const { t, i18n } = useTranslation("translation");
  const isArabic =
    String(i18n.language || "").toLowerCase().startsWith("ar") ||
    i18n.dir() === "rtl";
  const dateLocale = resolveDateLocale(i18n.language);
  const { can } = usePermission();
  const canExport = can(RBAC_MODULES.SHIPMENT_REPORTS, PERMISSION_ACTIONS.EXPORT);

  const [activeTab, setActiveTab] = useState("branch");
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterOptions, setFilterOptions] = useState({ carriers: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({});
  const [dateRange, setDateRange] = useState(defaultLast30Days);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedCarrier, setSelectedCarrier] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const pageRef = useRef(page);
  const limitRef = useRef(limit);
  pageRef.current = page;
  limitRef.current = limit;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadStores = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const stores = res.data?.stores || [];
      setStoresOptions(
        stores.map((s) => ({
          _id: s._id || s.id,
          name: s.name || s.name_en || s.displayName || "",
          name_en: s.name || s.name_en || s.displayName || "",
          name_ar: s.name_ar || s.displayNameAr || s.name || "",
          branches: s.branches || [],
        }))
      );
    } catch (error) {
      console.error("Failed to load stores for Shipment Reports", error);
    }
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchShipmentReports({
        page,
        limit,
        status: selectedStatus,
        carrier: selectedCarrier,
        storeId: selectedStoreId || undefined,
        branchId: selectedBranchId || undefined,
        dateFrom: dateRange.dateFrom || undefined,
        dateTo: dateRange.dateTo || undefined,
        search: debouncedSearch || undefined,
        sortBy: sortColumn || "createdAt",
        sortOrder: sortDirection || "desc",
      });
      setRows(result.data || []);
      setSummary(result.summary || {});
      setFilterOptions(result.filterOptions || { carriers: [] });
      setPagination(result.pagination || {});
    } catch (error) {
      console.error("Failed to load Shipment Reports", error);
      toast.error(t("shipmentReportsPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    selectedStatus,
    selectedCarrier,
    selectedStoreId,
    selectedBranchId,
    dateRange.dateFrom,
    dateRange.dateTo,
    debouncedSearch,
    sortColumn,
    sortDirection,
    t,
  ]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    const store = storesOptions.find((s) => s._id === selectedStoreId);
    if (!store) {
      setBranchesOptions([]);
      return;
    }
    setBranchesOptions(
      (store.branches || []).map((b) => ({
        _id: b._id || b.id,
        name: b?.name?.en || b?.name || b?.displayName || "",
        name_en: b?.name?.en || b?.name || b?.displayName || "",
        name_ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      }))
    );
  }, [selectedStoreId, storesOptions]);

  const reportStatuses = useMemo(
    () => [
      { id: "ALL", name: t("all") },
      { id: "Delivered", name: t("shipmentsPage.statusDelivered") },
      { id: "In Transit", name: t("shipmentsPage.statusInTransit") },
      { id: "Assigned", name: t("shipmentsPage.statusAssigned") },
      { id: "Pending", name: t("shipmentsPage.statusPending") },
      { id: "Cancelled", name: t("shipmentsPage.statusCancelled") },
    ],
    [t]
  );

  const carrierOptions = useMemo(
    () => [
      { _id: "ALL", name: t("all") },
      ...(filterOptions.carriers || []).map((carrier) => ({
        _id: carrier,
        name: carrier,
      })),
    ],
    [filterOptions.carriers, t]
  );

  const handleExport = async () => {
    if (!canExport) {
      toast.error(t("noPermission") || "No permission");
      return;
    }
    try {
      setIsExporting(true);
      const { blob, filename } = await fetchShipmentReportsExport({
        tab: activeTab,
        status: selectedStatus,
        carrier: selectedCarrier,
        storeId: selectedStoreId || undefined,
        branchId: selectedBranchId || undefined,
        dateFrom: dateRange.dateFrom || undefined,
        dateTo: dateRange.dateTo || undefined,
        search: debouncedSearch || undefined,
        sortBy: sortColumn || "createdAt",
        sortOrder: sortDirection || "desc",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting shipment reports:", err);
      toast.error(t("shipmentReportsPage.exportError"));
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowIndex",
        header: "#",
        minWidth: 48,
        width: 48,
        align: "center",
        headerAlign: "center",
        hideable: false,
        render: (_row, index) =>
          (pageRef.current - 1) * limitRef.current + index + 1,
      },
      {
        id: "brandBranch",
        header: t("shipmentsPage.brandAndBranch"),
        minWidth: 150,
        render: (row) => (
          <div
            className={`trackhub-reports-brand-branch${
              isArabic ? " trackhub-reports-brand-branch--rtl" : ""
            }`}
          >
            <span className="trackhub-reports-brand">{row.brand || "—"}</span>
            <span className="trackhub-reports-branch">{row.branch || "—"}</span>
          </div>
        ),
      },
      {
        id: "orderRef",
        header: t("shipmentsPage.orderId"),
        minWidth: 120,
        render: (row) => (
          <span className="trackhub-reports-mono" dir="ltr">
            {row.orderRef || "—"}
          </span>
        ),
      },
      {
        id: "deliveryId",
        header: t("shipmentsPage.deliveryId"),
        minWidth: 120,
        render: (row) => {
          const deliveryId = row.deliveryId || "—";
          return (
            <span dir="ltr">
              <TableCellEllipsis title={row.deliveryId ? deliveryId : undefined}>
                {deliveryId}
              </TableCellEllipsis>
            </span>
          );
        },
      },
      {
        id: "orderSource",
        header: t("shipmentsPage.orderSource"),
        minWidth: 130,
        render: (row) => formatOrderSource(row.orderSource, t),
      },
      {
        id: "shipmentId",
        header: t("shipmentsPage.shipmentId"),
        minWidth: 150,
        render: (row) => (
          <ShipmentIdCell
            shipmentId={row.shipmentId}
            copyLabel={t("shipmentsPage.copyShipmentId")}
            copiedLabel={t("shipmentsPage.shipmentIdCopied")}
          />
        ),
      },
      {
        id: "createdAt",
        header: t("shipmentsPage.date"),
        minWidth: 118,
        sortable: true,
        render: (row) => {
          const { date, time } = formatLocalizedDateTimeParts(
            row.createdAt,
            dateLocale,
            { withSeconds: false }
          );
          return (
            <div className="oh-table-date-cell" dir="ltr">
              <span className="oh-table-date-cell__date">{date}</span>
              <span className="oh-table-date-cell__time">{time}</span>
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("shipmentsPage.status"),
        minWidth: 118,
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
      id: "carrier",
      header: t("shipmentsPage.courier"),
      minWidth: 160,
      render: (row) => <CourierInfoCell row={row} />,
      },
      {
        id: "orderTotal",
        header: t("shipmentsPage.orderTotal"),
        minWidth: 112,
        align: "end",
        headerAlign: "end",
        nowrap: true,
        render: (row) => <PriceDisplay value={row.orderTotal ?? 0} />,
      },
      {
        id: "price",
        header: t("shipmentsPage.shipmentAmount"),
        minWidth: 148,
        align: "end",
        headerAlign: "end",
        nowrap: true,
        render: (row) => {
          const amount =
            row.restaurantShare != null && row.customerShare != null
              ? Number(row.restaurantShare) + Number(row.customerShare)
              : row.price ?? 0;
          return <PriceDisplay value={amount} />;
        },
      },
      {
        id: "restaurantShare",
        header: t("shipmentsPage.restaurantShare"),
        minWidth: 130,
        align: "end",
        headerAlign: "end",
        nowrap: true,
        render: (row) =>
          row.restaurantShare != null ? (
            <PriceDisplay value={row.restaurantShare} />
          ) : (
            "—"
          ),
      },
      {
        id: "customerShare",
        header: t("shipmentsPage.customerShare"),
        minWidth: 124,
        align: "end",
        headerAlign: "end",
        nowrap: true,
        render: (row) =>
          row.customerShare != null ? (
            <PriceDisplay value={row.customerShare} />
          ) : (
            "—"
          ),
      },
      {
        id: "customerPays",
        header: t("shipmentsPage.customerPaysTotal"),
        minWidth: 124,
        align: "end",
        headerAlign: "end",
        nowrap: true,
        render: (row) =>
          row.customerPays != null ? (
            <PriceDisplay value={row.customerPays} />
          ) : (
            "—"
          ),
      },
      {
        id: "matchedTierId",
        header: t("shipmentsPage.tierId"),
        minWidth: 88,
        align: "end",
        headerAlign: "end",
        nowrap: true,
        render: (row) => (
          <span className="trackhub-reports-mono">
            {row.tierId ?? row.matchedTierId ?? "—"}
          </span>
        ),
      },
    ],
    [dateLocale, isArabic, t]
  );

  const exportPrimaryAction = canExport
    ? {
        label: isExporting
          ? t("shipmentReportsPage.exporting")
          : t("shipmentReportsPage.export"),
        onClick: handleExport,
        disabled: isExporting,
        icon: (
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            style={{ transform: isArabic ? "scaleX(-1)" : "none" }}
          />
        ),
      }
    : undefined;

  const topBranchName = isArabic
    ? summary.topBranch?.nameAr || summary.topBranch?.name
    : summary.topBranch?.name;
  const topSourceName = summary.topSource?.key
    ? formatOrderSource(summary.topSource.key, t)
    : summary.topSource?.label || "—";

  return (
    <div className="trackhub-reports-page oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("shipmentReportsPage.title")}</h1>

        <div className="oh-page-section">
          <div className="oh-reports-tabs">
            <MDBRow className="mx-0 align-items-center">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <MDBCol
                    size="auto"
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="px-3"
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`pb-1 ${isActive ? "fw-bold" : "text-muted"}`}
                      style={{
                        fontSize: "0.9rem",
                        borderBottom: isActive
                          ? "3px solid #073B4C"
                          : "3px solid transparent",
                        transition: "all 0.2s ease-in-out",
                        color: isActive ? "#333333" : undefined,
                      }}
                    >
                      {t(tab.i18n)}
                    </div>
                  </MDBCol>
                );
              })}
            </MDBRow>
          </div>
        </div>

        <div className="oh-page-section">
          {activeTab === "branch" && (
            <div className="oh-kpi-row oh-kpi-row--4">
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.topBranchByVolume")}
                value={topBranchName || "—"}
                loading={loading}
                textValue
              />
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.totalShipments")}
                value={(summary.totalShipments ?? 0).toLocaleString()}
                loading={loading}
              />
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.totalSpend")}
                value={
                  <PriceDisplay
                    value={summary.totalSpend ?? 0}
                    iconSize={16}
                  />
                }
                loading={loading}
              />
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.avgSpendPerShipment")}
                value={
                  <PriceDisplay
                    value={summary.avgSpendPerShipment ?? 0}
                    iconSize={16}
                  />
                }
                loading={loading}
              />
            </div>
          )}
          {activeTab === "carrier" && (
            <div className="oh-kpi-row oh-kpi-row--3">
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.totalShipments")}
                value={(summary.totalShipments ?? 0).toLocaleString()}
                loading={loading}
              />
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.topCarrierByVolume")}
                value={summary.topCarrier?.name || "—"}
                loading={loading}
                textValue
              />
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.avgShipmentsPerCarrier")}
                value={(summary.avgShipmentsPerCarrier ?? 0).toLocaleString()}
                loading={loading}
              />
            </div>
          )}
          {activeTab === "source" && (
            <div className="oh-kpi-row oh-kpi-row--2">
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.topOrderingSource")}
                value={topSourceName || "—"}
                loading={loading}
                textValue
              />
              <OrderStatsCard
                title={t("shipmentReportsPage.cards.avgShipmentsPerCarrier")}
                value={(summary.avgShipmentsPerCarrier ?? 0).toLocaleString()}
                loading={loading}
              />
            </div>
          )}
        </div>

        <div className="oh-page-section">
          <Toolbar
            toolbarVariant="shipments"
            hideIcons
            showSearch
            searchQuery={searchQuery}
            handleSearch={(e) => setSearchQuery(e.target.value)}
            searchPlaceHolder={t("shipmentReportsPage.searchPlaceholder")}
            extraDropdowns={[
              {
                type: "date-range",
                label: t("datePresets.customRange"),
                placeholder: t("datePresets.last30"),
                value: [dateRange.dateFrom || null, dateRange.dateTo || null],
                onChange: (val) => {
                  const [from, to] = val || [];
                  setDateRange({
                    dateFrom: from || "",
                    dateTo: to || "",
                  });
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.brand"),
                placeholder: t("trackHubBranchesPage.allBrands"),
                options: storesOptions,
                selectedOption: selectedStoreId,
                showClear: true,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedStoreId(opt?._id || "");
                  setSelectedBranchId("");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.branch"),
                placeholder: selectedStoreId
                  ? t("trackHubBranchesPage.allBranches")
                  : t("shipmentsPage.selectBrandFirst"),
                options: branchesOptions,
                selectedOption: selectedBranchId,
                showClear: true,
                disabled: !selectedStoreId,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedBranchId(opt?._id || "");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.courier"),
                placeholder: t("shipmentsPage.courier"),
                options: carrierOptions,
                selectedOption: selectedCarrier || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedCarrier(opt?._id || "ALL");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.status"),
                placeholder: t("shipmentsPage.status"),
                options: reportStatuses.map((s) => ({ _id: s.id, name: s.name })),
                selectedOption: selectedStatus || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedStatus(opt?._id || "ALL");
                  setPage(1);
                },
              },
            ]}
          />

          <DataTable
            tableId="shipment-reports-v2"
            title={t("shipmentReportsPage.tableTitle")}
            columns={columns}
            data={rows}
            loading={loading}
            emptyState={t("shipmentReportsPage.empty")}
            primaryAction={exportPrimaryAction}
            showColumnSettings
            tableMinWidth={2200}
            isRtl={isArabic}
            sort={{
              column: sortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => {
                setSortColumn(column || "createdAt");
                setSortDirection(direction === "asc" ? "asc" : "desc");
                setPage(1);
              },
            }}
            pagination={{
              page,
              pageSize: limit,
              total: pagination.total || 0,
              onPageChange: setPage,
              onPageSizeChange: (size) => {
                setLimit(size);
                setPage(1);
              },
              isRtl: isArabic,
            }}
          />
        </div>
      </MDBContainer>
    </div>
  );
}

export default ShipmentReportsPage;
